import React, { useState } from "react";
import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { useFirebase } from "../../contexts/FirebaseContext";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

type SubmitAudienceCategoryRequest = {
  gameId: string;
  topic: string;
  submitterName: string;
};

export const Audience: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const { functions } = useFirebase();
  const toast = useToast();

  const [submitterName, setSubmitterName] = useState("");
  const [topic, setTopic] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const _submitAudienceCategory = httpsCallable<SubmitAudienceCategoryRequest, void>(
    functions,
    "submitAudienceCategory"
  );

  const handleSubmit = async () => {
    if (!topic.trim() || !submitterName.trim() || !gameId) return;

    setIsSubmitting(true);
    try {
      await _submitAudienceCategory({
        gameId,
        topic: topic.trim(),
        submitterName: submitterName.trim(),
      });

      toast({
        title: "Category submitted!",
        description: "Your category has been added for voting.",
        status: "success",
        duration: 3000,
      });

      setHasSubmitted(true);
      setTopic("");
    } catch (error: any) {
      console.error("Error submitting category:", error);
      toast({
        title: "Failed to submit",
        description: error?.message || "Please try again.",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = topic.trim() && submitterName.trim() && !isSubmitting;

  return (
    <Center
      width="100%"
      minHeight="100vh"
      backgroundColor="jeopardy.blue"
      p="2em"
    >
      <VStack spacing={8} width="100%" maxWidth="400px">
        <MotionBox
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Text
            fontSize={{ base: "2.5em", md: "4em" }}
            textStyle="jeoparty"
            textAlign="center"
          >
            JEOPARTY
          </Text>
          
        </MotionBox>

        <MotionBox
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          width="100%"
        >
          <VStack
            spacing={6}
            bg="rgba(0,0,0,0.3)"
            p={6}
            borderRadius="lg"
            width="100%"
          >
            <Text textStyle="clue" fontSize="sm" textAlign="center" opacity={0.8}>
              Submit a category idea for the players to vote on!
            </Text>

            <FormControl>
              <FormLabel textStyle="clue" fontSize="sm">
                Your Name
              </FormLabel>
              <Input
                value={submitterName}
                onChange={(e) => setSubmitterName(e.target.value)}
                backgroundColor="white"
                color="black"
                placeholder="Enter your name"
                maxLength={20}
              />
            </FormControl>

            <FormControl>
              <FormLabel textStyle="clue" fontSize="sm">
                Category Idea
              </FormLabel>
              <Input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                backgroundColor="white"
                color="black"
                placeholder="e.g., 90s Hip Hop, Marine Biology"
                maxLength={50}
              />
            </FormControl>

            <Button
              width="100%"
              size="lg"
              onClick={handleSubmit}
              isDisabled={!canSubmit}
              isLoading={isSubmitting}
              loadingText="Submitting..."
              background="jeopardy.gold"
              color="black"
              _hover={{ background: "yellow.400" }}
              _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
            >
              <Text textStyle="clue">Submit Category</Text>
            </Button>

            {hasSubmitted && (
              <Box
                bg="green.500"
                p={3}
                borderRadius="md"
                width="100%"
              >
                <Text textStyle="clue" fontSize="sm" textAlign="center">
                  Thanks for your submission! You can submit more ideas.
                </Text>
              </Box>
            )}
          </VStack>
        </MotionBox>

        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
        >
          <Text textStyle="clue" fontSize="xs" textAlign="center" opacity={0.5}>
            You're watching as an audience member
          </Text>
        </MotionBox>
      </VStack>
    </Center>
  );
};
